import {
  Activity,
  ArrowLeft,
  Book,
  Bot,
  CheckCircle2,
  Code,
  Copy,
  Database,
  FileText,
  Loader2,
  Lock,
  PlayCircle,
  Plug,
  RefreshCw,
  Server,
  Users,
  Zap,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { env } from '../config/env';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Modal,
} from '../components/ui';
import { ConnectorLogo } from '../components/connectors';
import { useOrganization, usePage } from '../contexts';
import { useConnectorSources } from '../hooks';
import { connectorSourceService } from '../services';
import { ConnectorProvider, ConnectorSource } from '../types/entities';
import { HttpError } from '../core/http/types';

const isHttpError = (error: unknown): error is HttpError =>
  typeof error === 'object' && error !== null && 'message' in error;

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (isHttpError(error) && typeof error.message === 'string' && error.message) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type EndpointDoc = {
  id: string;
  method: HttpMethod;
  path: string;
  summary: string;
  description: string;
  requiresApiKey: boolean;
  queryParams?: string[];
  pathParams?: string[];
  requestBody?: Record<string, unknown>;
  responseShape: string;
  notes?: string;
  isSse?: boolean;
};

type EndpointCategory = {
  id:
    | 'health'
    | 'agents'
    | 'tasks'
    | 'crews'
    | 'knowledge'
    | 'sources'
    | 'execute-ai';
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  endpoints: EndpointDoc[];
};

type ConnectorCatalogItem = {
  provider: ConnectorProvider;
  name: string;
  description: string;
  comingSoon?: boolean;
};

const CONNECTOR_CATALOG: ConnectorCatalogItem[] = [
  {
    provider: 'postgres',
    name: 'PostgreSQL',
    description: 'Banco relacional open source com suporte avançado a SQL.',
  },
  {
    provider: 'mysql',
    name: 'MySQL',
    description: 'Banco relacional amplamente adotado para aplicações web.',
  },
  {
    provider: 'elasticsearch',
    name: 'Elasticsearch',
    description: 'Motor de busca distribuído para indexação e análise.',
    comingSoon: true,
  },
  {
    provider: 'sankhya',
    name: 'Sankhya',
    description: 'ERP integrado para gestão de negócios.',
    comingSoon: true,
  },
  {
    provider: 'totvs',
    name: 'Totvs',
    description: 'Plataforma de software empresarial líder no Brasil.',
    comingSoon: true,
  },
];

const Integrations: React.FC = () => {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const { setPageInfo } = usePage();
  const navigate = useNavigate();

  const baseUrl = env.REACT_APP_API_URL;


  useEffect(() => {
    setPageInfo(t('integrations.title'), t('integrations.subtitle'));
  }, [setPageInfo, t]);

  const {
    connectorSources,
    isLoadingConnectors,
    loadConnectorSources,
  } = useConnectorSources(currentOrganization?.id, { activeOnly: false });
  const [isCreateConnectorModalOpen, setIsCreateConnectorModalOpen] = useState(false);
  const [createConnectorStep, setCreateConnectorStep] = useState<1 | 2>(1);
  const [selectedProvider, setSelectedProvider] = useState<ConnectorProvider | null>(
    null
  );
  const [displayName, setDisplayName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [database, setDatabase] = useState('');
  const [schema, setSchema] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSavingConnector, setIsSavingConnector] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      loadConnectorSources();
    }
  }, [currentOrganization?.id, loadConnectorSources]);

  const apiEndpoints = useMemo<EndpointCategory[]>(
    () => [
      {
        id: 'health',
        title: t('integrations.healthCheck'),
        description: t('integrations.checkApiStatus'),
        icon: Activity,
        endpoints: [
          {
            id: 'health-check',
            method: 'GET',
            path: '/v1/integrations/health',
            summary: t('integrations.checkApiStatus'),
            description:
              'Valida a API Key e retorna status do serviço de integrações.',
            requiresApiKey: true,
            responseShape: '{ status, service, organization_id, message }',
          },
        ],
      },
      {
        id: 'agents',
        title: t('integrations.agents'),
        description: t('integrations.manageAiAgents'),
        icon: Bot,
        endpoints: [
          {
            id: 'list-agents',
            method: 'GET',
            path: '/v1/integrations/agents',
            summary: t('integrations.listAgents'),
            description:
              'Lista agentes da organização da API Key, com paginação.',
            requiresApiKey: true,
            queryParams: ['page', 'page_size'],
            responseShape:
              '{ items: Agent[], page, page_size, total, total_pages }',
          },
          {
            id: 'get-agent',
            method: 'GET',
            path: '/v1/integrations/agents/{agent_id}',
            summary: t('integrations.getAgentById'),
            description: 'Retorna um agente específico pelo ID.',
            requiresApiKey: true,
            pathParams: ['agent_id'],
            responseShape: 'AgentResponse',
          },
          {
            id: 'create-agent',
            method: 'POST',
            path: '/v1/integrations/agents',
            summary: t('integrations.createNewAgent'),
            description: 'Cria um novo agente para a organização.',
            requiresApiKey: true,
            requestBody: {
              name: 'Meu Agente',
              role: 'Especialista em Vendas',
              goal: 'Ajudar com análises comerciais',
              backstory: 'Agente treinado para responder com contexto',
              tools: ['codeinterpretertool'],
              is_active: true,
              organization_id: 1,
            },
            responseShape: 'AgentResponse (201)',
          },
          {
            id: 'update-agent',
            method: 'PUT',
            path: '/v1/integrations/agents/{agent_id}',
            summary: t('integrations.updateAgent'),
            description: 'Atualiza campos de um agente existente.',
            requiresApiKey: true,
            pathParams: ['agent_id'],
            requestBody: {
              name: 'Agente Atualizado',
              goal: 'Meta atualizada',
            },
            responseShape: 'AgentResponse',
          },
          {
            id: 'delete-agent',
            method: 'DELETE',
            path: '/v1/integrations/agents/{agent_id}',
            summary: t('integrations.removeAgent'),
            description: 'Desativa o agente (soft delete).',
            requiresApiKey: true,
            pathParams: ['agent_id'],
            responseShape: '{ message }',
          },
        ],
      },
      {
        id: 'tasks',
        title: t('integrations.tasks'),
        description: t('integrations.manageAgentTasks'),
        icon: FileText,
        endpoints: [
          {
            id: 'list-tasks',
            method: 'GET',
            path: '/v1/integrations/tasks',
            summary: t('integrations.listTasks'),
            description:
              'Lista tarefas da organização; aceita filtro opcional por agente.',
            requiresApiKey: true,
            queryParams: ['agent_id', 'page', 'page_size'],
            responseShape:
              '{ items: Task[], page, page_size, total, total_pages }',
          },
          {
            id: 'get-task',
            method: 'GET',
            path: '/v1/integrations/tasks/{task_id}',
            summary: t('integrations.getTaskById'),
            description: 'Retorna uma tarefa específica pelo ID.',
            requiresApiKey: true,
            pathParams: ['task_id'],
            responseShape: 'TaskResponse',
          },
          {
            id: 'create-task',
            method: 'POST',
            path: '/v1/integrations/tasks',
            summary: t('integrations.createNewTask'),
            description: 'Cria uma nova tarefa para um agente.',
            requiresApiKey: true,
            requestBody: {
              description: 'Analisar pipeline comercial',
              expected_output: 'Resumo dos riscos e oportunidades',
              agent_id: 1,
              tools: ['codeinterpretertool'],
              context: [],
              is_active: true,
            },
            responseShape: 'TaskResponse (201)',
          },
          {
            id: 'update-task',
            method: 'PUT',
            path: '/v1/integrations/tasks/{task_id}',
            summary: t('integrations.updateTask'),
            description: 'Atualiza uma tarefa existente.',
            requiresApiKey: true,
            pathParams: ['task_id'],
            requestBody: {
              description: 'Descrição atualizada',
            },
            responseShape: 'TaskResponse',
          },
          {
            id: 'delete-task',
            method: 'DELETE',
            path: '/v1/integrations/tasks/{task_id}',
            summary: t('integrations.removeTask'),
            description: 'Desativa a tarefa (soft delete).',
            requiresApiKey: true,
            pathParams: ['task_id'],
            responseShape: '{ message }',
          },
        ],
      },
      {
        id: 'crews',
        title: t('integrations.crews'),
        description: t('integrations.manageAgentTeams'),
        icon: Users,
        endpoints: [
          {
            id: 'list-crews',
            method: 'GET',
            path: '/v1/integrations/crews',
            summary: t('integrations.listTeams'),
            description: 'Lista fluxos/equipes da organização.',
            requiresApiKey: true,
            queryParams: ['page', 'page_size'],
            responseShape:
              '{ items: Crew[], page, page_size, total, total_pages }',
          },
          {
            id: 'get-crew',
            method: 'GET',
            path: '/v1/integrations/crews/{crew_id}',
            summary: t('integrations.getTeamById'),
            description: 'Retorna um fluxo/equipe por ID.',
            requiresApiKey: true,
            pathParams: ['crew_id'],
            responseShape: 'CrewResponse',
          },
          {
            id: 'create-crew',
            method: 'POST',
            path: '/v1/integrations/crews',
            summary: t('integrations.createNewTeam'),
            description: 'Cria uma nova equipe com agentes e tarefas.',
            requiresApiKey: true,
            requestBody: {
              name: 'Fluxo Comercial',
              description: 'Equipe focada em oportunidades de venda',
              organization_id: 1,
              agent_ids: [1],
              task_ids: [1],
              is_active: true,
            },
            responseShape: 'CrewResponse (201)',
          },
          {
            id: 'update-crew',
            method: 'PUT',
            path: '/v1/integrations/crews/{crew_id}',
            summary: t('integrations.updateTeam'),
            description: 'Atualiza uma equipe existente.',
            requiresApiKey: true,
            pathParams: ['crew_id'],
            requestBody: {
              name: 'Fluxo Comercial v2',
            },
            responseShape: 'CrewResponse',
          },
          {
            id: 'delete-crew',
            method: 'DELETE',
            path: '/v1/integrations/crews/{crew_id}',
            summary: t('integrations.removeTeam'),
            description: 'Desativa a equipe (soft delete).',
            requiresApiKey: true,
            pathParams: ['crew_id'],
            responseShape: '{ message }',
          },
        ],
      },
      {
        id: 'knowledge',
        title: t('integrations.knowledgeSources'),
        description: t('integrations.manageKnowledgeBase'),
        icon: Book,
        endpoints: [
          {
            id: 'list-knowledge',
            method: 'GET',
            path: '/v1/integrations/knowledge-sources',
            summary: t('integrations.listKnowledgeSources'),
            description: 'Lista fontes de conhecimento da organização.',
            requiresApiKey: true,
            queryParams: ['page', 'page_size'],
            responseShape:
              '{ items: KnowledgeSource[], page, page_size, total, total_pages }',
          },
          {
            id: 'get-knowledge',
            method: 'GET',
            path: '/v1/integrations/knowledge-sources/{source_id}',
            summary: t('integrations.getSourceById'),
            description: 'Obtém uma fonte de conhecimento por ID.',
            requiresApiKey: true,
            pathParams: ['source_id'],
            responseShape: 'KnowledgeSourceResponse',
          },
          {
            id: 'create-knowledge',
            method: 'POST',
            path: '/v1/integrations/knowledge-sources',
            summary: t('integrations.createKnowledgeSource'),
            description:
              'Endpoint presente para contrato, mas responde erro orientando uso do upload específico.',
            requiresApiKey: true,
            requestBody: {
              name: 'Documento exemplo',
              organization_id: 1,
              tags: ['manual'],
            },
            responseShape: 'HTTP 400 (orientação para endpoint de upload)',
          },
          {
            id: 'update-knowledge',
            method: 'PUT',
            path: '/v1/integrations/knowledge-sources/{source_id}',
            summary: t('integrations.updateKnowledgeSource'),
            description: 'Atualiza metadados de uma fonte existente.',
            requiresApiKey: true,
            pathParams: ['source_id'],
            requestBody: {
              name: 'Documento atualizado',
              tags: ['manual', '2026'],
            },
            responseShape: 'KnowledgeSourceResponse',
          },
          {
            id: 'delete-knowledge',
            method: 'DELETE',
            path: '/v1/integrations/knowledge-sources/{source_id}',
            summary: t('integrations.removeKnowledgeSource'),
            description: 'Exclui uma fonte de conhecimento.',
            requiresApiKey: true,
            pathParams: ['source_id'],
            responseShape: '{ message }',
          },
          {
            id: 'external-ingest',
            method: 'POST',
            path: '/v1/integrations/knowledge-sources/external-ingest',
            summary: 'Ingestão externa de conteúdo',
            description:
              'Cria/atualiza conteúdo externo e dispara processamento assíncrono.',
            requiresApiKey: true,
            requestBody: {
              source: 'crm',
              event_type: 'upsert',
              external_id: 'deal-001',
              content: {
                title: 'Resumo da oportunidade',
                text: 'Detalhes da negociação...',
              },
              tags: ['crm', 'negociacao'],
            },
            responseShape:
              '{ knowledge_source_id, organization_id, status, job_id, idempotency_key } (202)',
            notes:
              'Dependente da configuração de ingestão externa habilitada no backend.',
          },
        ],
      },
      {
        id: 'sources',
        title: 'Integration Sources',
        description: 'Cadastro e sincronização de conectores externos.',
        icon: Server,
        endpoints: [
          {
            id: 'list-sources',
            method: 'GET',
            path: '/v1/integrations/sources',
            summary: 'Listar integrações cadastradas',
            description:
              'Lista conectores por organização com filtros de provider e status.',
            requiresApiKey: true,
            queryParams: ['provider', 'is_active', 'page', 'page_size'],
            responseShape:
              '{ items: IntegrationSource[], page, page_size, total, total_pages }',
          },
          {
            id: 'create-source',
            method: 'POST',
            path: '/v1/integrations/sources',
            summary: 'Criar integração externa',
            description:
              'Registra uma nova integração (provider + configuração).',
            requiresApiKey: true,
            requestBody: {
              provider: 'notion',
              display_name: 'Notion Workspace',
              config: {
                token: '***',
              },
            },
            responseShape: 'IntegrationSourceResponse (201)',
          },
          {
            id: 'update-source',
            method: 'PATCH',
            path: '/v1/integrations/sources/{source_id}',
            summary: 'Atualizar integração externa',
            description: 'Atualiza configuração e estado de um conector.',
            requiresApiKey: true,
            pathParams: ['source_id'],
            requestBody: {
              display_name: 'Notion Workspace (prod)',
              is_active: true,
            },
            responseShape: 'IntegrationSourceResponse',
          },
          {
            id: 'sync-source',
            method: 'POST',
            path: '/v1/integrations/sources/{source_id}/sync',
            summary: 'Sincronizar integração',
            description: 'Executa sincronização imediata do conector.',
            requiresApiKey: true,
            pathParams: ['source_id'],
            responseShape: '{ id, organization_id, status, synced_at }',
          },
        ],
      },
      {
        id: 'execute-ai',
        title: 'Execute AI',
        description:
          'Execução de crew com resposta em streaming (SSE), incluindo modo live.',
        icon: PlayCircle,
        endpoints: [
          {
            id: 'execute-ai',
            method: 'POST',
            path: '/v1/integration/execute-ai',
            summary: 'Executar crew com streaming SSE',
            description:
              'Inicia execução sequencial e transmite eventos de status e resultado.',
            requiresApiKey: true,
            requestBody: {
              crew_id: 1,
              user_message: 'Resuma os principais riscos do pipeline.',
              use_canvas: false,
              source_ids: [1, 2],
              search_context_type: 'global',
              session_id: 'session-001',
            },
            responseShape: 'text/event-stream (events: message, done)',
            isSse: true,
            notes:
              'Use leitor de stream no cliente; não é resposta JSON única.',
          },
          {
            id: 'execute-ai-live',
            method: 'POST',
            path: '/v1/integration/execute-ai/live',
            summary: 'Executar crew em modo live',
            description:
              'Mantém stream aberto para recomputações em janela configurável.',
            requiresApiKey: true,
            requestBody: {
              crew_id: 1,
              user_message: 'Monitore alterações e atualize a resposta.',
              use_canvas: false,
              source_ids: [],
              search_context_type: 'global',
              live_window_seconds: 300,
              max_recomputations: 10,
            },
            responseShape: 'text/event-stream contínuo (status e atualizações)',
            isSse: true,
            notes: 'Recomendado para cenários de acompanhamento contínuo.',
          },
        ],
      },
    ],
    [t]
  );

  const [activeCategory, setActiveCategory] = useState(apiEndpoints[0]?.id);

  const getMethodColor = (method: HttpMethod) => {
    switch (method) {
      case 'GET':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'POST':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'PATCH':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'DELETE':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('integrations.copiedToClipboard'));
  };

  const buildFetchExample = (endpoint: EndpointDoc) => {
    const body = endpoint.requestBody
      ? `,\n  body: JSON.stringify(${JSON.stringify(endpoint.requestBody, null, 2)})`
      : '';

    return `fetch('${baseUrl}${endpoint.path}', {
  method: '${endpoint.method}',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY_HERE'
  }${body}
});`;
  };

  const buildCurlExample = (endpoint: EndpointDoc) => {
    const body = endpoint.requestBody
      ? ` \\\n  -d '${JSON.stringify(endpoint.requestBody)}'`
      : '';

    return `curl -X ${endpoint.method} '${baseUrl}${endpoint.path}' \\
  -H 'X-API-Key: YOUR_API_KEY_HERE' \\
  -H 'Content-Type: application/json'${body}`;
  };

  const getProviderMeta = (provider: ConnectorProvider) =>
    CONNECTOR_CATALOG.find(item => item.provider === provider) || {
      provider,
      name: provider,
    };

  const resetCreateConnectorModal = () => {
    setCreateConnectorStep(1);
    setSelectedProvider(null);
    setDisplayName('');
    setHost('');
    setPort('');
    setDatabase('');
    setSchema('');
    setUsername('');
    setPassword('');
  };

  const closeCreateConnectorModal = () => {
    setIsCreateConnectorModalOpen(false);
    resetCreateConnectorModal();
  };

  const handleSelectProvider = (provider: ConnectorProvider, comingSoon?: boolean) => {
    if (comingSoon) {
      toast('Conector disponível em breve.');
      return;
    }
    setSelectedProvider(provider);
    setCreateConnectorStep(2);
  };

  const handleCreateConnector = async () => {
    if (!selectedProvider) {
      toast.error('Selecione um conector.');
      return;
    }
    if (!displayName.trim()) {
      toast.error('Informe o nome de exibição.');
      return;
    }
    if (!host.trim() || !database.trim() || !username.trim() || !password) {
      toast.error('Preencha host, database, usuário e senha.');
      return;
    }
    setIsSavingConnector(true);
    try {
      await connectorSourceService.create({
        provider: selectedProvider,
        display_name: displayName.trim(),
        scope_type: 'organization',
        credentials: {
          username: username.trim(),
          password,
        },
        metadata: {
          host: host.trim(),
          port: port ? Number(port) : undefined,
          database: database.trim(),
          schema: schema.trim() || undefined,
        },
      });
      await loadConnectorSources();
      toast.success('Conector criado com sucesso.');
      closeCreateConnectorModal();
    } catch (error) {
      if (isHttpError(error) && error.status === 409) {
        toast.error(
          'Já existe um conector com este nome para este provider. ' +
            'Verifique a lista (inclusive os inativos) ou escolha outro nome.'
        );
      } else {
        toast.error(extractErrorMessage(error, 'Falha ao criar conector.'));
      }
    } finally {
      setIsSavingConnector(false);
    }
  };

  const handleTestConnector = async (source: ConnectorSource) => {
    try {
      const result = await connectorSourceService.test(source.id);
      if (result.success) {
        toast.success(`Conexão OK${result.latency_ms ? ` (${result.latency_ms}ms)` : ''}`);
      } else {
        toast.error(result.message || 'Teste de conexão falhou.');
      }
      await loadConnectorSources();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Falha ao testar conector.'));
    }
  };

  const handleSyncConnector = async (source: ConnectorSource) => {
    try {
      await connectorSourceService.sync(source.id);
      toast.success('Sincronização iniciada.');
      await loadConnectorSources();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Falha ao sincronizar conector.'));
    }
  };

  const handleToggleConnector = async (source: ConnectorSource) => {
    try {
      await connectorSourceService.update(source.id, {
        is_active: !source.is_active,
      });
      toast.success(source.is_active ? 'Conector desativado.' : 'Conector ativado.');
      await loadConnectorSources();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Falha ao atualizar conector.'));
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Plug className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                Conectores
              </CardTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Lista de conectores cadastrados para uso em conversas e integrações.
              </p>
            </div>
            <Button onClick={() => setIsCreateConnectorModalOpen(true)}>
              Adicionar conector
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingConnectors ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando conectores...
            </div>
          ) : connectorSources.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-6 py-10 text-center dark:border-gray-700 dark:bg-gray-900/40">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <Database className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Nenhum conector cadastrado.
              </p>
              <p className="max-w-md text-xs text-gray-500 dark:text-gray-400">
                Adicione conectores para que seus agentes possam consultar bancos
                e sistemas externos diretamente durante uma conversa.
              </p>
              <Button
                size="sm"
                onClick={() => setIsCreateConnectorModalOpen(true)}
              >
                Adicionar primeiro conector
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {connectorSources.map(source => {
                const providerMeta = getProviderMeta(source.provider);
                return (
                  <div
                    key={source.id}
                    className="group flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-primary-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-900/70 dark:hover:border-primary-500/60"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border ${
                          source.is_active
                            ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-500/40 dark:bg-primary-500/10 dark:text-primary-300'
                            : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300'
                        }`}
                      >
                        <ConnectorLogo
                          provider={source.provider}
                          className="h-6 w-6"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                            {source.display_name}
                          </p>
                          <Badge
                            className={
                              source.is_active
                                ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                            }
                          >
                            {source.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {providerMeta.name} • {source.scope_type}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestConnector(source)}
                      >
                        Testar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSyncConnector(source)}
                      >
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                        Sync
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleConnector(source)}
                      >
                        {source.is_active ? 'Desativar' : 'Ativar'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-primary-600" />
            Integração com API
          </CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('integrations.documentation')}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => navigate('/organization/my-apis')}
            >
              {t('organization.manageMyApis')}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                {t('integrations.baseUrl')}
              </p>
              <p className="mt-2 font-mono text-sm break-all">{baseUrl}</p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Auth
              </p>
              <p className="mt-2 text-sm">Header `X-API-Key` obrigatório</p>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-300">
            Paginação padrão para listagens: query params `page` e `page_size`.
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Code className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('integrations.integrationEndpoints')}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {apiEndpoints.reduce((acc, cat) => acc + cat.endpoints.length, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Zap className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('integrations.categories')}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {apiEndpoints.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="p-3 bg-violet-100 dark:bg-violet-900 rounded-lg">
                <PlayCircle className="h-6 w-6 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  SSE
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  2
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('integrations.categories')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {apiEndpoints.map(category => (
              <Button
                key={category.id}
                size="sm"
                variant={activeCategory === category.id ? 'primary' : 'outline'}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.title} ({category.endpoints.length})
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {apiEndpoints
          .filter(category => !activeCategory || category.id === activeCategory)
          .map(category => {
            const CategoryIcon = category.icon;

            return (
              <Card key={category.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CategoryIcon className="h-5 w-5 text-primary-600" />
                    {category.title}
                  </CardTitle>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {category.endpoints.map(endpoint => (
                    <div
                      key={endpoint.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={getMethodColor(endpoint.method)}>
                          {endpoint.method}
                        </Badge>
                        <code className="text-xs md:text-sm font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          {endpoint.path}
                        </code>
                        {endpoint.requiresApiKey && (
                          <Badge variant="warning">
                            {t('integrations.requiresApiKey')}
                          </Badge>
                        )}
                        {endpoint.isSse && <Badge variant="info">SSE</Badge>}
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {endpoint.summary}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {endpoint.description}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
                          <p className="font-medium mb-1">Path params</p>
                          <p className="text-gray-600 dark:text-gray-400">
                            {endpoint.pathParams?.length
                              ? endpoint.pathParams.join(', ')
                              : '-'}
                          </p>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
                          <p className="font-medium mb-1">Query params</p>
                          <p className="text-gray-600 dark:text-gray-400">
                            {endpoint.queryParams?.length
                              ? endpoint.queryParams.join(', ')
                              : '-'}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-md bg-gray-900 text-gray-100 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-400">
                            {t('integrations.url')}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-200 border-gray-500 hover:bg-gray-800"
                            onClick={() =>
                              copyToClipboard(`${baseUrl}${endpoint.path}`)
                            }
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            {t('integrations.copy')}
                          </Button>
                        </div>
                        <code className="text-xs md:text-sm break-all">
                          {baseUrl}
                          {endpoint.path}
                        </code>
                      </div>

                      {endpoint.requestBody && (
                        <div className="rounded-md bg-gray-900 text-gray-100 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-400">
                              {t('integrations.body')}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-200 border-gray-500 hover:bg-gray-800"
                              onClick={() =>
                                copyToClipboard(
                                  JSON.stringify(endpoint.requestBody, null, 2)
                                )
                              }
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              {t('integrations.copy')}
                            </Button>
                          </div>
                          <pre className="text-xs whitespace-pre-wrap">
                            {JSON.stringify(endpoint.requestBody, null, 2)}
                          </pre>
                        </div>
                      )}

                      <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Response
                        </p>
                        <p className="text-sm">{endpoint.responseShape}</p>
                      </div>

                      {endpoint.notes && (
                        <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
                          {endpoint.notes}
                        </div>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div className="rounded-md bg-gray-900 text-gray-100 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-400">cURL</span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-200 border-gray-500 hover:bg-gray-800"
                              onClick={() =>
                                copyToClipboard(buildCurlExample(endpoint))
                              }
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              {t('integrations.copy')}
                            </Button>
                          </div>
                          <pre className="text-xs whitespace-pre-wrap">
                            {buildCurlExample(endpoint)}
                          </pre>
                        </div>

                        <div className="rounded-md bg-gray-900 text-gray-100 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-400">
                              {t('integrations.codeExample')}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-200 border-gray-500 hover:bg-gray-800"
                              onClick={() =>
                                copyToClipboard(buildFetchExample(endpoint))
                              }
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              {t('integrations.copy')}
                            </Button>
                          </div>
                          <pre className="text-xs whitespace-pre-wrap">
                            {buildFetchExample(endpoint)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
      </div>

      <Modal
        isOpen={isCreateConnectorModalOpen}
        onClose={closeCreateConnectorModal}
        title={
          createConnectorStep === 1
            ? 'Adicionar conector'
            : `Configurar ${getProviderMeta(selectedProvider || 'postgres').name}`
        }
        size="lg"
      >
        <div className="space-y-5">
          <div className="flex items-center gap-3 text-xs font-medium">
            <div
              className={`flex items-center gap-2 ${
                createConnectorStep >= 1
                  ? 'text-primary-700 dark:text-primary-300'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                  createConnectorStep >= 1
                    ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-500/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                {createConnectorStep > 1 ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  '1'
                )}
              </span>
              Escolher conector
            </div>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            <div
              className={`flex items-center gap-2 ${
                createConnectorStep >= 2
                  ? 'text-primary-700 dark:text-primary-300'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                  createConnectorStep >= 2
                    ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-500/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                2
              </span>
              Configurar
            </div>
          </div>

          {createConnectorStep === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Escolha o tipo de conector que deseja integrar.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {CONNECTOR_CATALOG.map(item => {
                  const isComingSoon = Boolean(item.comingSoon);
                  return (
                    <button
                      key={item.provider}
                      type="button"
                      onClick={() =>
                        handleSelectProvider(item.provider, item.comingSoon)
                      }
                      disabled={isComingSoon}
                      className={`group relative flex flex-col gap-3 rounded-xl border bg-white p-4 text-left transition-all duration-200 dark:bg-gray-900/70 ${
                        isComingSoon
                          ? 'cursor-not-allowed border-gray-200 opacity-70 dark:border-gray-700'
                          : 'border-gray-200 hover:-translate-y-0.5 hover:border-primary-400 hover:bg-primary-50/40 hover:shadow-md dark:border-gray-700 dark:hover:border-primary-500/70 dark:hover:bg-primary-500/5'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-700 transition-colors group-hover:border-primary-200 group-hover:bg-primary-50 group-hover:text-primary-700 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-200 dark:group-hover:border-primary-500/40 dark:group-hover:bg-primary-500/10 dark:group-hover:text-primary-300">
                          <ConnectorLogo
                            provider={item.provider}
                            className="h-6 w-6"
                          />
                        </div>
                        {isComingSoon && (
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                            <Lock className="mr-1 h-3 w-3" />
                            Em breve
                          </Badge>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {item.name}
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={closeCreateConnectorModal}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedProvider && (
                <div className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50/60 p-3 dark:border-primary-500/30 dark:bg-primary-500/10">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-primary-700 shadow-sm dark:bg-gray-900 dark:text-primary-300">
                    <ConnectorLogo provider={selectedProvider} className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {getProviderMeta(selectedProvider).name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Informe os dados de conexão abaixo.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Nome de exibição
                </label>
                <Input
                  value={displayName}
                  onChange={event => setDisplayName(event.target.value)}
                  placeholder="Ex.: Banco de Produção"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Host
                  </label>
                  <Input
                    value={host}
                    onChange={event => setHost(event.target.value)}
                    placeholder="db.exemplo.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Porta
                  </label>
                  <Input
                    value={port}
                    onChange={event => setPort(event.target.value)}
                    placeholder="5432"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Database
                  </label>
                  <Input
                    value={database}
                    onChange={event => setDatabase(event.target.value)}
                    placeholder="meu_banco"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Schema{' '}
                    <span className="text-gray-400 dark:text-gray-500">
                      (opcional)
                    </span>
                  </label>
                  <Input
                    value={schema}
                    onChange={event => setSchema(event.target.value)}
                    placeholder="public"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Usuário
                  </label>
                  <Input
                    value={username}
                    onChange={event => setUsername(event.target.value)}
                    placeholder="usuario"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Senha
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-between gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                <Button
                  variant="outline"
                  onClick={() => setCreateConnectorStep(1)}
                  disabled={isSavingConnector}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Voltar
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={closeCreateConnectorModal}
                    disabled={isSavingConnector}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleCreateConnector}
                    disabled={isSavingConnector}
                  >
                    {isSavingConnector ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      'Salvar conector'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

    </div>
  );
};

export default Integrations;
